import { useState, useEffect, useCallback } from "react";
import { Eye, Users, TrendingUp, Loader2, RefreshCw, Monitor, Smartphone, Globe, FileText } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const fmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
};

export default function VisitorAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/analytics"); setData(data); }
    catch { toast.error("Failed to load analytics"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="p-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-flame" /></div>;
  if (!data) return null;

  const maxDay = Math.max(1, ...data.timeline.map((d) => d.count));
  const t = data.totals;

  return (
    <div data-testid="analytics-panel">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display font-bold text-xl text-white">Website Visitors</h2>
        <button onClick={load} data-testid="analytics-refresh" className="inline-flex items-center gap-1.5 text-xs text-flame hover:underline"><RefreshCw className="h-3.5 w-3.5" /> Refresh</button>
      </div>

      <div className="grid gap-4 grid-cols-2 lg:grid-cols-5 mb-8">
        {[
          { label: "Total Views", value: t.views, icon: Eye },
          { label: "Unique Visitors", value: t.unique_sessions, icon: Users },
          { label: "Views Today", value: t.today, icon: TrendingUp },
          { label: "Views (7d)", value: t.last7, icon: Eye },
          { label: "Visitors (7d)", value: t.unique_7d, icon: Users },
        ].map((c) => (
          <div key={c.label} data-testid={`analytics-stat-${c.label.toLowerCase().replace(/\W+/g, "-")}`} className="bg-ink border border-white/10 p-5">
            <div className="flex items-center justify-between"><c.icon className="h-5 w-5 text-flame" /><span className="font-display font-black text-3xl">{c.value}</span></div>
            <div className="mt-2 text-[11px] text-white/50 uppercase tracking-widest">{c.label}</div>
          </div>
        ))}
      </div>

      <div className="bg-ink border border-white/10 p-6 mb-8">
        <div className="text-xs text-white/50 uppercase tracking-widest mb-4">Last 14 Days</div>
        <div className="flex items-end gap-1.5 h-40" data-testid="analytics-timeline">
          {data.timeline.map((d) => (
            <div key={d.date} className="flex-1 flex flex-col items-center gap-1 group">
              <div className="w-full bg-flame/20 hover:bg-flame transition-colors relative" style={{ height: `${(d.count / maxDay) * 100}%`, minHeight: d.count ? "3px" : "0" }}>
                <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-[10px] text-white/70 opacity-0 group-hover:opacity-100">{d.count}</span>
              </div>
              <span className="text-[8px] text-white/30 whitespace-nowrap">{d.date.slice(5)}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3 mb-8">
        <div className="bg-ink border border-white/10 p-6">
          <div className="flex items-center gap-2 text-xs text-white/50 uppercase tracking-widest mb-4"><FileText className="h-4 w-4 text-flame" /> Top Pages</div>
          <div className="space-y-2" data-testid="analytics-top-pages">
            {data.top_pages.length === 0 && <div className="text-white/30 text-sm">No data yet</div>}
            {data.top_pages.map((p) => (
              <div key={p.value} className="flex items-center justify-between text-sm">
                <span className="text-white/70 truncate mr-2">{p.value}</span>
                <span className="text-flame font-semibold">{p.count}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-ink border border-white/10 p-6">
          <div className="flex items-center gap-2 text-xs text-white/50 uppercase tracking-widest mb-4"><Globe className="h-4 w-4 text-flame" /> Top Referrers</div>
          <div className="space-y-2" data-testid="analytics-top-referrers">
            {data.top_referrers.length === 0 && <div className="text-white/30 text-sm">Mostly direct traffic</div>}
            {data.top_referrers.map((r) => (
              <div key={r.value} className="flex items-center justify-between text-sm">
                <span className="text-white/70 truncate mr-2">{r.value}</span>
                <span className="text-flame font-semibold">{r.count}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-ink border border-white/10 p-6">
          <div className="flex items-center gap-2 text-xs text-white/50 uppercase tracking-widest mb-4"><Monitor className="h-4 w-4 text-flame" /> Devices</div>
          <div className="space-y-2" data-testid="analytics-devices">
            {data.devices.map((d) => (
              <div key={d.value} className="flex items-center justify-between text-sm">
                <span className="text-white/70 capitalize inline-flex items-center gap-1.5">{d.value === "mobile" ? <Smartphone className="h-3.5 w-3.5" /> : <Monitor className="h-3.5 w-3.5" />}{d.value}</span>
                <span className="text-flame font-semibold">{d.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-ink border border-white/10">
        <div className="p-5 border-b border-white/10 text-xs text-white/50 uppercase tracking-widest">Recent Visits</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="analytics-recent-table">
            <tbody>
              {data.recent.map((r, i) => (
                <tr key={i} className="border-t border-white/5 text-white/80">
                  <td className="px-5 py-2.5 text-white">{r.path}</td>
                  <td className="px-5 py-2.5 text-white/50 text-xs">{r.referrer}</td>
                  <td className="px-5 py-2.5 text-white/50 text-xs capitalize">{r.device}</td>
                  <td className="px-5 py-2.5 text-white/50 text-xs whitespace-nowrap text-right">{fmt(r.created_at)}</td>
                </tr>
              ))}
              {data.recent.length === 0 && <tr><td className="px-5 py-8 text-center text-white/30">No visits recorded yet</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
